<template>
  <div>
    <div v-if="!previewMode && filteredProducts.length <= 0" class="no-products-found">
      <span>{{ getNoProductsFoundText }}</span>
    </div>
    <div v-else class="main-container">
      <div class="label-quantity-row">
        <span>{{ label }}</span>
        <number-chip :number="allProducts.length" />
      </div>

      <div v-if="!previewMode" class="description-row">
        <span> {{ getDescriptionText }} </span>
      </div>

      <div v-for="product in filteredProducts" :key="product.sku" class="product-information" :class="{ 'border-bottom': !previewMode }">
        <div class="img-container">
          <img v-if="product.image_url" :src="product.image_url" alt="imagem do produto" width="100%" />
          <mf-icon v-else size="36">image_not_supported</mf-icon>
        </div>
        <div class="flex-column-gap-6">
          <span class="product-name">{{ product.name }}</span>
          <div class="flex-row-gap-22">
            <span class="cinza-0-14px"><strong>SKU: </strong>{{ product.sku }}</span>
            <span v-if="product.eans.length === 1" class="cinza-0-14px"><strong>EAN: </strong>{{ product.ean }}</span>
            <div v-else>
              <span class="link"><strong>EANs: </strong>{{ product.eans.length }}</span>
            </div>
            <span v-if="mode === 'trigger'" class="cinza-0-14px"><strong>Quant. mínima: </strong>{{ offerSale.trigger_rules.trigger_amount }}</span>
            <span v-else class="discount-text"><strong>Desconto: </strong>{{ offerSale.target_rules.discount }}%</span>
          </div>
        </div>
      </div>

      <div v-if="allProducts.length - 1 > 0 && previewMode" class="blue-chip">
        <span>{{ getBlueChipText }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import NumberChip from '../NumberChip.vue'
import { normalizeText } from '@/modules/industria/helpers/searchHelper.js'

export default {
  components: {
    NumberChip,
  },
  props: {
    offerSale: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ['trigger', 'target'].includes(value),
    },
    search: {
      type: String,
      default: '',
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    localSearch: '',
  }),
  computed: {
    label() {
      return this.mode === 'trigger' ? 'Produtos gatilho' : 'Produtos com desconto'
    },
    filteredProducts() {
      if (this.previewMode) {
        return this.allProducts.slice(0, 1)
      }
      return this.allProducts.filter((product) => {
        const searched = normalizeText(this.localSearch)
        return (
          normalizeText(product.name).includes(searched) || product.sku.includes(searched) || product.ean.includes(searched) || product.eans.includes(searched)
        )
      })
    },
    allProducts() {
      return this.mode === 'trigger' ? this.offerSale.trigger_products : this.offerSale.target_products
    },
    getBlueChipText() {
      const quantity = this.allProducts.length - 1
      if (this.mode === 'trigger') {
        return quantity > 1 ? `+ ${quantity} Produtos gatilho` : '+ 1 Produto gatilho'
      } else {
        return quantity > 1 ? `+ ${quantity} Produtos com desconto` : '+ 1 Produto com desconto'
      }
    },
    getDescriptionText() {
      if (this.mode === 'trigger') {
        return `Produtos que ativam o desconto do combo. O consumidor pode comprar qualquer combinação dos produtos gatilho para atingir a quantidade mínima indicada.`
      } else {
        return `Quando os critérios para ativar o desconto forem atingidos, o consumidor vai ter acesso aos produtos listados, respeitando o mínimo para ativar o desconto e o mínimo por CPF.`
      }
    },
    getNoProductsFoundText() {
      if (this.mode === 'trigger') {
        return 'Nenhum produto gatilho encontrado com a sua busca.'
      } else {
        return 'Nenhum produto com desconto encontrado com a sua busca.'
      }
    },
  },
  watch: {
    search() {
      this.localSearch = this.search
    },
  },
}
</script>

<style lang="scss" scoped>
.no-products-found {
  display: flex;
  justify-content: center;
}

.main-container {
  border-left: 3px solid $cinza-l60;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  @media screen and (max-width: $xs-breakpoint) {
    padding: 16px;
    border-left: unset;
    border-bottom: 1px solid $cinza-l60;
    align-items: center;
    width: 100%;
  }

  .label-quantity-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    & span {
      font-size: 16px;
      font-weight: bold;
      color: $cinza-0;
    }
  }

  .description-row {
    font-size: 14px;
    font-weight: normal;
    color: $cinza-0;
  }

  .border-bottom {
    border-bottom: 1px solid $cinza-l60;
  }

  .product-information {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding-top: 16px;
    padding-bottom: 16px;
    width: 80%;

    @media screen and (max-width: $xs-breakpoint) {
      flex-direction: column;
      width: 100%;
    }

    &:last-child {
      border-bottom: none;
    }

    .img-container {
      width: 74px;
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;

      & img {
        display: block;
        max-width: 100%;
        max-height: 100%;
        mix-blend-mode: darken;
        object-fit: contain;
      }
    }

    .flex-column-gap-6 {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .product-name {
        font-size: 1rem;
        font-weight: bold;
        color: $cinza-d10;

        @media screen and (max-width: $xs-breakpoint) {
          text-align: center;
        }
      }

      .flex-row-gap-22 {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 22px;

        .cinza-0-14px {
          font-size: 14px;
          color: $cinza-0;
        }

        .discount-text {
          font-size: 14px;
          color: $verde-0;
        }
      }
    }
  }

  .blue-chip {
    padding: 5px 12px;
    border-radius: 16px;
    font-size: 16px;
    background: #d2e9ff80;
    color: $marca-0-l10;
    white-space: nowrap;
    width: fit-content;
    user-select: none;
  }
}
</style>
